import { Box, List, Typography, IconButton, Tooltip } from '@mui/material';
import { createPortal } from 'react-dom';
import { useState, useEffect } from 'react';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { tooltipSxElevated } from '../../components/shared/jernyTooltipStyling';
import calEventEditEngine from '../Core/CalEventEditEngine';
import { useCalendar } from '../useCalendar';
import UpcomingDrawerItem from './UpcomingDrawerItem';
import { drawerSx, drawerHeaderSx, drawerTitleSx, drawerEmptySx, drawerPagerSx, drawerPagerLabelSx } from './upcomingDrawerSx';

const PAGE_SIZE = 5;

/**
 * UpcomingDrawer — floating panel with all upcoming events.
 * Own createPortal div, no MUI Popover — JInspector pattern.
 * Bottom-right corner of the drawer is pinned to the top-right corner of
 * the opener (Windows 8 start-menu behavior — expands up/left).
 * Renders even when upcoming is empty — shows "No upcoming events".
 * Paginated at PAGE_SIZE items per page — pager only shown when the list
 * doesn't fit on a single page. Page resets to 0 every time the drawer
 * (re)opens, via the same effect that positions it against opener.
 *
 * display:none until pos is calculated — avoids a flash at {bottom:0,right:0}
 * on the first render after opening.
 *
 * Props:
 *   opener   — DOM element the drawer is aligned to, or null (= closed)
 *   onClose  — closes the drawer (local UI state owned by MiniFooter)
 */
export default function UpcomingDrawer({ opener, onClose }) {
    const { upcoming } = useCalendar();
    const [pos, setPos] = useState({ bottom: 0, right: 0 });
    const [page, setPage] = useState(0);

    // Calculates position relative to opener — drawer's bottom-right corner
    // pinned to opener's top-right corner. Also resets to page 0 on every
    // (re)open, so a stale page from a previous open never sticks around.
    useEffect(() => {
        if (!opener) return;
        const rect = opener.getBoundingClientRect();
        setPos({
            bottom: window.innerHeight - rect.top + 4,
            right: window.innerWidth - rect.right,
            height: window.innerHeight,
            width: window.innerWidth,
        });
        setPage(0);
    }, [opener]);

    const totalPages = Math.max(1, Math.ceil(upcoming.length / PAGE_SIZE));
    // Clamps page if the list shrank (e.g. dismiss) while sitting on a later page.
    const safePage = Math.min(page, totalPages - 1);
    const pageItems = upcoming.slice(safePage * PAGE_SIZE, safePage * PAGE_SIZE + PAGE_SIZE);

    /**
     * Opens the reminder editor for a brand new event.
     */
    const handleNewEvent = () => {
        calEventEditEngine.startEdit('__new__');
    };

    /**
     * Goes to the previous page, if any.
     */
    const handlePrevPage = () => {
        setPage((p) => Math.max(0, p - 1));
    };

    /**
     * Goes to the next page, if any.
     */
    const handleNextPage = () => {
        setPage((p) => Math.min(totalPages - 1, p + 1));
    };

    if (!opener) return null;

    const drawer = (
        <Box sx={{ ...drawerSx, bottom: pos.bottom, right: pos.right, display: pos.bottom ? 'block' : 'none' }}>
            <Box sx={drawerHeaderSx}>
                <Typography sx={drawerTitleSx}>Upcoming</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Tooltip title="New Event" componentsProps={tooltipSxElevated}>
                        <IconButton size="small" onClick={handleNewEvent} sx={{ p: '3px', color: '#fff' }}>
                            <AddIcon sx={{ fontSize: 14 }} />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Close" componentsProps={tooltipSxElevated}>
                        <IconButton size="small" onClick={onClose} sx={{ p: '3px', color: '#fff' }}>
                            <CloseIcon sx={{ fontSize: 14 }} />
                        </IconButton>
                    </Tooltip>
                </Box>
            </Box>
            {upcoming.length === 0 ? (
                <Typography sx={drawerEmptySx}>No upcoming events</Typography>
            ) : (
                <List dense disablePadding>
                    {pageItems.map((e) => (
                        <UpcomingDrawerItem key={e.rk} event={e} />
                    ))}
                </List>
            )}
            {upcoming.length > PAGE_SIZE && (
                <Box sx={drawerPagerSx}>
                    <IconButton size="small" onClick={handlePrevPage} disabled={safePage === 0} sx={{ p: '2px', color: '#a3d1ff' }}>
                        <ChevronLeftIcon sx={{ fontSize: 14 }} />
                    </IconButton>
                    <Typography sx={drawerPagerLabelSx}>
                        {safePage + 1} / {totalPages}
                    </Typography>
                    <IconButton
                        size="small"
                        onClick={handleNextPage}
                        disabled={safePage === totalPages - 1}
                        sx={{ p: '2px', color: '#a3d1ff' }}
                    >
                        <ChevronRightIcon sx={{ fontSize: 14 }} />
                    </IconButton>
                </Box>
            )}
        </Box>
    );

    return createPortal(drawer, document.body);
}
